/**
 * ── THE LEADERBOARD: the analytics page's first TABULAR card (VERA-260) ─────────────
 *
 * Every other card on this page draws a SCALAR — one figure, a series of figures, or a
 * set of points. This one draws a DISTRIBUTION: scans grouped by owner, product or
 * place, ranked. That is the gap the table display exists to close, and the reason is
 * not presentational. A scalar hides its own shape — "average resales per product" is
 * the same number whether one product resold forty times or forty resold once each, and
 * the average is precisely the statistic that destroys the difference a brand needs.
 *
 * ## ⚠️ WHAT THIS CARD DOES NOT CLAIM
 *
 * It is titled for scan ACTIVITY, not for influence, and `ITopInfluencers`' header sets
 * out why at length: Vera records no attribution chain. Nothing on a scan says who
 * caused it — no `shared_by`, no session, no `utm_*`, no share token; `referer` is the
 * raw HTTP header and an NFC tap is a direct navigation, so it is usually null. There is
 * no link from a scan to a transfer or to an order, so no scan can be said to have
 * CAUSED anything.
 *
 * An owner high on this list may simply have carried the item somewhere busy. The card
 * says "whose certificates got scanned most", which is true, rather than "who drove the
 * most interest", which is not knowable. Copy that overstates a number is how a brand
 * learns to stop believing the numbers next to it.
 *
 * ## Grid sorting is OFF; ranking is a QUERY parameter (`rankBy`)
 *
 * The server returns the top N **on one key**, and re-sorting those N in the grid would
 * answer a different question while looking like it answered this one: it gives "the ten
 * biggest by scans, ordered by leads", which is not "the ten biggest by leads" — an owner
 * with 400 leads and 3 scans never enters a scans-ranked top ten, so no rearrangement of
 * the response can surface them. A ranking truncated on one key cannot be honestly
 * re-ranked on another client-side.
 *
 * This section used to end "if per-key ranking is wanted, it belongs in the QUERY (an
 * `order_by` parameter), not in the grid." VERA-260 built exactly that: `rankBy` is sent
 * to the endpoint, which orders and truncates on it. So the two halves are settled rather
 * than in tension — ranking moves, sorting stays off, and the card states which ranking it
 * is showing (the `rankBy` column leads the numeric columns, and the description names it)
 * so a reader is never left inferring the order from the numbers.
 *
 * ⚠️ `rankBy` is a property of the CARD, not a control. Each rankable metric generates its
 * own leaderboard card (`analyticsDisplayVariants`) with its key fixed by
 * `ROI_CARD_RANK_KEY`; the in-card toggle changes the GROUPING axis only. A second control
 * for the ranking would put two cards' worth of state in one card and make the title —
 * which names the metric — disagree with the rows.
 */
import React, { useMemo } from 'react'

import { Box, Link, Stack, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'

import type { TopInfluencerDimension, TopInfluencerRankKey } from '@repo/types'

import { EmptyState } from 'components/display/EmptyState'
import { Table } from 'components/display/Table/Table'
import { headerWithTerm } from 'components/display/Table/headerWithTerm'
import { ViewToggle } from 'components/input/ViewToggle'
import { useAnalyticsTopInfluencers } from 'hooks/api'
import { useReferrerState } from 'hooks/useReferrerState'
import { ROUTES } from 'routes'
import type { AnalyticsFilters } from 'store/analytics/types'
import { relativeOrDateTime } from 'utils'

import { Table2Icon } from 'lucide-react'

/**
 * The dimensions the card offers, in the order the toggle shows them.
 *
 * ⚠️ TOTAL over `TopInfluencerDimension` — a value added to that union is a compile
 * error here until it is given a label and a link rule, which is the intent. A dimension
 * the server can rank but the client cannot NAME would render a column of raw UUIDs, and
 * a dimension it cannot LINK would render a row that looks clickable and is not.
 */
export const TOP_INFLUENCER_DIMENSION_LABELS: Record<TopInfluencerDimension, string> = {
  owner: 'Owners',
  product: 'Products',
  country: 'Countries',
  city: 'Cities',
}

/**
 * The toggle's order. Derived from the labels map's own key order rather than restated,
 * so a dimension can never appear in one and not the other.
 *
 * `owner` first because it is the card's reason for existing — the other three are
 * breakdowns of the same scans that happen to be free from the same query.
 */
export const TOP_INFLUENCER_DIMENSION_ORDER = Object.keys(
  TOP_INFLUENCER_DIMENSION_LABELS,
) as TopInfluencerDimension[]

/** The default axis a freshly-placed leaderboard card opens on. */
export const DEFAULT_TOP_INFLUENCER_DIMENSION: TopInfluencerDimension = 'owner'

/** The column header for the entity column — singular, because it names one row. */
const ENTITY_HEADER: Record<TopInfluencerDimension, string> = {
  owner: 'Owner',
  product: 'Product',
  country: 'Country',
  city: 'City',
}

/**
 * Where a row links to, or `null` for a dimension with no record behind it.
 *
 * `country` and `city` are deliberately null: a place is not a record in Vera, so there
 * is no page to open. They could link to a filtered list, but that would be a DIFFERENT
 * filter from the one this row was computed under (the leaderboard's counts come from
 * the `scans` table, the certificates list filters certificates) — a destination whose
 * numbers disagree with the row that sent you there is worse than no destination.
 */
const ROW_ROUTE: Record<TopInfluencerDimension, ((key: string) => string) | null> = {
  owner: ROUTES.OWNER,
  product: ROUTES.PRODUCT,
  country: null,
  city: null,
}

/**
 * The entity cell — rank, name, and a second line.
 *
 * ⚠️ FIRST hand-roll, deliberately (§36 allows one; the second is a shared component).
 * The existing shared cells cannot serve here: `ProductCell` and `UserBlock` take a
 * fully-hydrated product/owner row (images, avatar, status) and this endpoint returns
 * only what it can GROUP BY — a key, a label and a sublabel. Fetching whole records to
 * satisfy a cell would turn a ten-row aggregate into ten record lookups.
 *
 * The trigger for extraction is stated so it is not a judgement call later: the moment a
 * SECOND ranked-breakdown surface needs this shape, it becomes
 * `components/display/RankedEntityCell`.
 */
const EntityCell = ({
  rank,
  label,
  sublabel,
  to,
}: {
  rank: number
  label: string
  sublabel: string | null
  to: string | null
}) => {
  // §42 — a lateral jump to another record hands the destination its way back. Wired at
  // the same time as `to`, never as a follow-up.
  const referrer = useReferrerState()

  const name = to ? (
    <Link
      component={RouterLink}
      to={to}
      state={referrer}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      sx={{
        textDecoration: 'none',
        color: 'text.primary',
        '&:hover': { textDecoration: 'underline' },
        minWidth: 0,
      }}
    >
      <Typography variant="body3" sx={{ fontWeight: 500 }} noWrap>
        {label}
      </Typography>
    </Link>
  ) : (
    <Typography variant="body3" sx={{ fontWeight: 500 }} noWrap>
      {label}
    </Typography>
  )

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={3}
      sx={{
        height: '100%',
        minWidth: 0,
        /*
          ⚠️ `lineHeight: 'normal'` IS LOAD-BEARING — without it the second line is
          clipped, and the cause is not obvious from the markup.

          MUI's DataGrid centres ordinary single-line cell content by setting the CELL's
          `line-height` to the full row height (measured: 71px on a 72px row). That
          inherits, and the `<Link>` below is a `display: block` element with no
          line-height of its own, so its line box became 71px tall — 18px of text in a
          71px box. The name block then measured 89px inside a 72px cell whose overflow is
          `hidden`, so the email sat 9px past the bottom edge and was cut in half.

          Resetting it here re-scopes the whole subtree to normal leading; each
          `Typography` then supplies its own. Measured after: 18 + 18 = 36px, centred.
        */
        lineHeight: 'normal',
      }}
    >
      {/*
        The rank is part of this cell rather than its own column. A separate `#` column
        would need its own header, its own width and its own §49 measurement, all to
        carry a number that is only meaningful adjacent to the name it ranks — and it
        would be the one column whose value changes meaning if the grid were ever sorted
        (see the header on why it is not).
      */}
      <Typography
        variant="body3"
        color="text.secondary"
        sx={{ width: 18, flexShrink: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
      >
        {rank}
      </Typography>
      <Stack sx={{ minWidth: 0 }}>
        {name}
        {sublabel && (
          <Typography variant="body4" color="text.secondary" noWrap>
            {sublabel}
          </Typography>
        )}
      </Stack>
    </Stack>
  )
}

/** A right-aligned count. Tabular figures so the digits line up down the column. */
const NumberCell = ({ value }: { value: number }) => (
  <Stack sx={{ height: '100%' }} justifyContent="center" alignItems="flex-end">
    <Typography variant="body3" sx={{ fontVariantNumeric: 'tabular-nums' }}>
      {value.toLocaleString()}
    </Typography>
  </Stack>
)

export interface TopInfluencersProps {
  dimension: TopInfluencerDimension
  onDimensionChange: (next: TopInfluencerDimension) => void
  /**
   * Which column the server ranks and truncates on. Fixed per card, never a control —
   * see the file header. Defaults to `scans`, matching the endpoint's own default, so an
   * existing call site keeps its behaviour.
   */
  rankBy?: TopInfluencerRankKey
  filters: AnalyticsFilters
  /** The card is maximized or filling its grid cell — either way, take all the height. */
  expanded?: boolean
}

export const TopInfluencers = ({
  dimension,
  onDimensionChange,
  rankBy = 'scans',
  filters,
}: TopInfluencersProps) => {
  const { data, isLoading } = useAnalyticsTopInfluencers({
    from: filters.dateRange.from.toISOString(),
    to: filters.dateRange.to.toISOString(),
    dimension,
    rank_by: rankBy,
    owner_ids: filters.ownerIds,
    product_ids: filters.productIds,
    certificate_ids: filters.certificateIds,
    certificate_statuses: filters.certificateStatuses,
    location: filters.location,
  })

  const rows = data?.rows ?? []
  const route = ROW_ROUTE[dimension]

  /*
    ── THE RANKED COLUMN COMES FIRST ────────────────────────────────────────────────

    The numeric columns are ordered so the one the rows are RANKED on sits immediately
    right of the name. Sorting is off (see the file header), so the column order is the
    only thing on the grid that says which number produced this order — leave Scans first
    on a leads-ranked card and the table reads as ranked by scans, with the actual ranking
    visible only as a descending sequence one column over that a reader has to notice.

    Everything after the ranked pair keeps its authored order, so the scans-ranked card is
    byte-for-byte the layout it has always had.
  */
  const rankedFirst = <T extends { field: string }>(numeric: T[]): T[] => [
    ...numeric.filter(c => c.field === rankBy),
    ...numeric.filter(c => c.field !== rankBy),
  ]

  const columns = useMemo(
    () => [
      {
        field: 'label',
        headerName: ENTITY_HEADER[dimension],
        type: 'string',
        // Takes the slack so the four numeric columns keep their measured widths and the
        // name — the only value that can legitimately ellipsize (§49: a CELL may clip, a
        // HEADER may not) — absorbs a narrow card.
        flex: 1,
        minWidth: 220,
        sortable: false,
        renderCell: ({ row }: { row: any }) => (
          <EntityCell
            rank={rows.indexOf(row) + 1}
            label={row.label}
            sublabel={row.sublabel}
            to={route ? route(row.key) : null}
          />
        ),
      },
      ...rankedFirst([
      {
        field: 'scans',
        headerName: 'Scans',
        type: 'number',
        // §49 — sized for the HEADER, not the values. "Scans" + the (i) glyph (14px + a
        // 4px gap) + the grid's cell padding; the counts themselves are far narrower.
        width: 110,
        sortable: false,
        renderHeader: headerWithTerm('Scans', 'scan'),
        renderCell: ({ row }: { row: any }) => <NumberCell value={row.scans} />,
      },
      {
        field: 'leads',
        headerName: 'Leads',
        type: 'number',
        width: 110,
        sortable: false,
        // "Lead" is a term of art with a precise, already-shipped definition (a scanner
        // who is not one of this company's owners). §44 — the sentence comes from the
        // glossary, never retyped here.
        renderHeader: headerWithTerm('Leads', 'lead'),
        renderCell: ({ row }: { row: any }) => <NumberCell value={row.leads} />,
      },
      ]),
      {
        field: 'certificates',
        headerName: 'Certificates',
        type: 'number',
        // The widest header on the card. 150 rather than the 110 its siblings take —
        // "Certificates" is 12 characters against "Scans"' 5, and §49's whole point is
        // that a number column is sized by the word above it.
        width: 150,
        sortable: false,
        renderCell: ({ row }: { row: any }) => <NumberCell value={row.certificates} />,
      },
      {
        field: 'last_scan_at',
        headerName: 'Last scan',
        type: 'string',
        width: 140,
        sortable: false,
        renderCell: ({ row }: { row: any }) => (
          <Stack sx={{ height: '100%' }} justifyContent="center">
            <Typography variant="body3" color="text.secondary" noWrap>
              {relativeOrDateTime(row.last_scan_at) ?? '—'}
            </Typography>
          </Stack>
        ),
      },
    ],
    [dimension, route, rows, rankBy],
  )

  return (
    <Stack sx={{ height: '100%', minHeight: 0 }}>
      {/*
        ── THE GROUP-BY ROW LIVES IN THE CARD BODY, NOT THE HEADER ────────────────────

        🚨 Measured, not preferred. It WAS in the shell's header `action` slot, beside
        the title, and at this card's half-width default (§ `size.w = 6`) that row does
        not fit: `CardSectionHeader` puts the action in a `flexShrink: 0` box and the
        title/description in a `minWidth: 0` one, so the action never gives way and the
        title is the thing that collapses. Measured on a 594px header row — action slot
        365px + card controls 196px + gap = 573px — which left the title and description
        box **5 pixels wide**, wrapping the description one word per line down the card.

        Two things made it unfittable together, and both are wanted: the control has FOUR
        word-labelled options (no conventional glyphs exist for four grouping axes, see
        the toggle below), and it now carries a "Group by:" label. Neither is negotiable,
        so the row is.

        Below the header it gets a full-width line of its own, which is also where a
        control over a table's ROWS conventionally belongs — a toolbar above the grid
        rather than a card-title adornment. The card's TITLE still names the current axis
        ("Scans by owners"), so the header did not lose the information, only the control.

        ⚠️ Do NOT "restore" this to the header slot. It fits at full width and collapses
        the title at half, which means it would look correct on the card you tested and
        break on the default one.
      */}
      <Box sx={{ flexShrink: 0, pb: 3 }}>
        <TopInfluencersDimensionToggle value={dimension} onChange={onDimensionChange} />
      </Box>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Table
          rows={rows}
          columns={columns}
          getRowId={(row: any) => row.key}
          /*
            §19 — the shared Table renders a column-aware skeleton from `loading`, so the
            placeholder occupies the same grid the rows will. `isLoading` (first load)
            rather than `isFetching`: the hook keeps the previous ranking on screen while
            a filter change refetches, and flashing a skeleton over readable data on every
            date nudge is the thing that rule exists to prevent.
          */
          loading={isLoading}
          /*
            An EMBEDDED panel, so all the page-table chrome is off. The ActionLine brings a
            search box, filters, sort, layout and download — a second toolbar inside a card
            that already has a header, duplicating filters the page owns globally.

            ⚠️ NO `fillBottom`. That is for a table that IS the page (§22), and it measures
            against the VIEWPORT bottom — inside a resizable grid cell it would size the
            table to the window and overflow the card. `fillParent` is the counterpart for
            exactly this case: pure CSS flex against the parent's definite height, which
            `AnalyticsCardShell` provides (`flex: 1; minHeight: 0`).
          */
          showActionLine={false}
          showSelection={false}
          showFooter={false}
          hideExport
          fillParent
          emptyState={
            <EmptyState
              // Worded for the ranking, not for the card: an empty leads-ranked table is
              // "nobody new has scanned", which a reader can act on, where "no scan
              // activity" would be flatly wrong on a period that had plenty of scans and
              // no new scanners.
              title={rankBy === 'leads' ? 'No new scanners yet' : 'No scan activity yet'}
              text={
                rankBy === 'leads'
                  ? 'Once someone outside your own owners scans a certificate in this period, they will be ranked here.'
                  : 'Once certificates are scanned in this period, the most-scanned will be ranked here.'
              }
              icon={<Table2Icon strokeWidth={1} size={32} />}
            />
          }
        />
      </Box>
      {/*
        The unrankable remainder, stated rather than dropped.

        A leaderboard that silently discards the scans it could not group reads as a
        complete account of the window. Under `owner` the excluded set is real and often
        large — a brand-held certificate has no owner, so every scan before an item is
        sold lands here — and a reader comparing this card's column to the Total Scans
        tile deserves to know where the difference went. Rendered only when non-zero: a
        line reading "0 scans could not be ranked" is noise on every card that has none.
      */}
      {(data?.unattributed_scans ?? 0) > 0 && (
        <Typography variant="body4" color="text.secondary" sx={{ pt: 2, lineHeight: 1.5 }}>
          {data?.unattributed_scans.toLocaleString()} more{' '}
          {data?.unattributed_scans === 1 ? 'scan has' : 'scans have'} no{' '}
          {dimension === 'owner' ? 'owner' : dimension === 'product' ? 'product' : 'location'}{' '}
          recorded, so {data?.unattributed_scans === 1 ? 'it is' : 'they are'} not ranked above.
        </Typography>
      )}
    </Stack>
  )
}

/**
 * The card header's breakdown control — which axis the scans are grouped on.
 *
 * `ViewToggle`, not `SegmentedTabs`, and `ScansByLocationModeToggle` already paid for
 * that lesson twice over: the segmented pill is the control for switching WHAT IS
 * COUNTED (the page's own filters, which change every figure on screen), while this
 * changes only how THIS card's rows are grouped. Same chrome for two very different
 * consequences overstates the smaller one and dilutes the larger.
 *
 * Words rather than icons: four grouping axes have no conventional glyphs, and an icon
 * whose meaning has to be learned from its tooltip is worse than the word it replaced.
 *
 * ## ⚠️ THE "Group by:" LABEL IS NOT DECORATION — four nouns alone do not state a question
 *
 * Bare, the control reads `Owners | Products | Countries | Cities`: four plural nouns in a
 * card header, which is the same shape as a set of TABS (four things to look at) and gives
 * a reader no way to tell that picking one REGROUPS the rows below rather than navigating
 * somewhere. Every other `ViewToggle` on this page escapes that ambiguity by having only
 * two options whose relationship is self-evident (line/bar, all/latest); at four, the
 * relationship has to be said.
 *
 * The label says it in the one word — "group" — that names what the choice does to the
 * table, which is exactly what the accessible name has always carried in `ariaLabel`. That
 * asymmetry is the tell that the label was missing: a screen-reader user was told the
 * group's purpose and a sighted user was not. The two now agree (`ariaLabel` moved from
 * "Rank by" to "Group by" in the same change) rather than describing the control two ways.
 *
 * ⚠️ FIRST hand-roll of a labelled toggle, deliberately (§36 allows one). `ViewToggle`
 * takes no `label` prop and is NOT given one here: it is a shared control used by four
 * other call sites that do not want a label, and adding an optional prop to serve one
 * consumer is the change §47 warns against making before a second consumer exists. The
 * extraction trigger is stated so it is not a judgement call later — the moment a SECOND
 * display needs a "Group by" control (the chart's status split and the map's location
 * grouping are the live candidates; see this file's Group-by proposal), the label moves
 * INTO `ViewToggle` as an optional `label` prop and all three read it from there.
 */
export const TopInfluencersDimensionToggle = ({
  value,
  onChange,
}: {
  value: TopInfluencerDimension
  onChange: (next: TopInfluencerDimension) => void
}) => (
  <Stack direction="row" alignItems="center" spacing={2}>
    {/*
      `component="span"`, not a `<label>`: a `<label>` promises a single labelled form
      control, and this names a GROUP of four buttons. The group's accessible name is
      carried by `ViewToggle`'s own `role="group"` + `aria-label`, so this text is
      presentational for a screen reader and would be read twice if it were exposed —
      hence `aria-hidden`.

      `body3` + `text.secondary` is the same weight the unselected options render at, so
      the label reads as the question and the options as the answers, rather than as a
      fifth item in the row.
    */}
    <Typography
      component="span"
      aria-hidden
      variant="body3"
      color="text.secondary"
      sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
    >
      Group by:
    </Typography>
    <ViewToggle
      options={TOP_INFLUENCER_DIMENSION_ORDER.map(dimension => ({
        value: dimension,
        label: TOP_INFLUENCER_DIMENSION_LABELS[dimension],
      }))}
      value={value}
      onChange={onChange}
      // Was "Rank by". The visible label and the accessible name must say the same thing,
      // and "group" is the accurate verb: the server's RANKING is always by scans (see the
      // file header on why sorting is off), and what this control changes is what the rows
      // are grouped INTO.
      ariaLabel="Group by"
    />
  </Stack>
)

/**
 * The card's description line — per dimension AND per ranking key.
 *
 * Both axes, because the sentence is wrong if it names only one. "The owners whose
 * certificates were scanned most" on a leads-ranked card describes an ordering the card
 * is not showing, and a description that contradicts the rows is worse than none: a
 * reader who trusts it mis-reads every row beneath it.
 *
 * The leads wording says "brought the most new scanners", never "referred" or "drove" —
 * `ITopInfluencers`' header is explicit that Vera records no attribution chain (no
 * `shared_by`, no session, no `utm_*`, and an NFC tap is a direct navigation). A lead is
 * a distinct scanner email that is not one of this company's own owners; an owner high on
 * this list reached people who were new to the brand, which is a fact, rather than caused
 * them to arrive, which is not knowable.
 */
export const topInfluencersDescription = (
  dimension: TopInfluencerDimension,
  rankBy: TopInfluencerRankKey = 'scans',
): string => {
  if (rankBy === 'leads') {
    switch (dimension) {
      case 'owner':
        return 'The owners whose certificates reached the most new scanners in the selected period. A lead is someone who scanned and is not one of your own owners. Reach, not attribution — Vera does not record who prompted a scan.'
      case 'product':
        return 'The products that reached the most new scanners in the selected period. A lead is someone who scanned and is not one of your own owners.'
      case 'country':
        return 'Where new scanners came from in the selected period, by country.'
      case 'city':
        return 'Where new scanners came from in the selected period, by city.'
    }
  }
  switch (dimension) {
    case 'owner':
      return 'The owners whose certificates were scanned most in the selected period. Exposure, not attribution — Vera does not record who prompted a scan.'
    case 'product':
      return 'The products whose certificates were scanned most in the selected period.'
    case 'country':
      return 'Where scans happened in the selected period, by country.'
    case 'city':
      return 'Where scans happened in the selected period, by city.'
  }
}
